import { useEffect, useState, type RefObject } from "react";

// =============================================================================
// useInView — one-shot "has this element entered the viewport?"
// =============================================================================
// Scroll-reveal primitive for the ANIMATION TOOLKIT (see /animate skill).
// Fires TRUE the first time the element intersects the viewport, then
// disconnects — reveals are one-shot, scrolling away and back does not
// re-trigger (matches the .anim-* mount-animation semantics).
//
// IntersectionObserver's default root is the viewport, which works regardless
// of which nested .overflow-y-auto container actually scrolls — no scroll
// listeners, no container discovery.
// =============================================================================

export function useInView<T extends Element>(ref: RefObject<T | null>): boolean {
  // No IntersectionObserver (very old browser / non-DOM env) → reveal immediately.
  const [inView, setInView] = useState(() => typeof IntersectionObserver === "undefined");

  useEffect(() => {
    if (inView) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      // Fire as soon as any part of the element is visible — reveals should
      // feel eager, not laggy.
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView, ref]);

  return inView;
}
